import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Webcam, Maximize, Camera, Timer, ShieldAlert, CameraOff } from "lucide-react";

export default function ConsentPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const applicationId = params.get("app");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const startedRef = useRef(false);
  const [durA, setDurA] = useState(30);
  const [durB, setDurB] = useState(15);

  // Durate lette dalle impostazioni dell'istituto (con maggiorazione DSA se prevista)
  useEffect(() => {
    if (!applicationId) return;
    api.get("/applications/mine").then(({ data }) => {
      const app = (data || []).find((x) => x.id === applicationId);
      if (app?.test_durations) {
        setDurA(app.test_durations.section_a);
        setDurB(app.test_durations.section_b);
      }
    }).catch(() => {});
  }, [applicationId]);

  const RULES = [
    { icon: Webcam, text: "La webcam deve essere accessibile e attiva: il test non può iniziare senza webcam. Durante la prova, mantieni lo sguardo sullo schermo. Il sistema acquisisce periodicamente degli snapshot." },
    { icon: Maximize, text: "Il test si svolge esclusivamente a schermo intero. Se esci dallo schermo intero appare un avviso: hai 3 secondi per rientrare, altrimenti il test viene annullato." },
    { icon: ShieldAlert, text: "Altre applicazioni, schede del browser e strumenti di supporto non sono consentiti durante la sessione." },
    { icon: CameraOff, text: "È vietato effettuare screenshot o registrazioni dello schermo durante il test. La violazione comporta l'esclusione dalla selezione." },
    { icon: Timer, text: `Sezione A: ${durA} minuti a risposta chiusa. Sezione B: ${durB} minuti di scheda orientativa. Al termine riceverai solo la conferma di completamento: gli esiti saranno comunicati dall'istituto.` },
    { icon: Camera, text: "Risposte, tempi, eventi di sessione e snapshot vengono registrati e saranno valutati dalla commissione." },
  ];

  const start = async () => {
    if (startedRef.current) return;
    startedRef.current = true;
    setBusy(true);
    try {
      const { data } = await api.post("/tests/start", { application_id: applicationId });
      localStorage.setItem("its_test_session", data.id);
      if (data.status === "section_b_active") {
        navigate("/test/sezione-b");
      } else {
        navigate("/test");
      }
    } catch (e) {
      startedRef.current = false;
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <p className="eyebrow mb-2">Istruzioni e informativa proctoring</p>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
          Sessione d'esame controllata
        </h1>
        <p className="mt-3 text-base text-slate-600 leading-relaxed">
          Il test di ammissione si svolge in modalità <strong>proctored</strong>: webcam attiva, schermo intero
          obbligatorio e registrazione degli eventi di sessione, a garanzia della regolarità della selezione.
        </p>

        <Card className="institutional-card mt-8">
          <CardContent className="p-6 sm:p-8">
            <ul className="space-y-5">
              {RULES.map((r, i) => (
                <li key={i} data-testid={`proctor-rule-${i}`} className="flex items-start gap-4">
                  <div className="h-9 w-9 shrink-0 rounded-lg bg-[#E0E7FF] text-[#003366] flex items-center justify-center">
                    <r.icon className="h-4 w-4" />
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed pt-1.5">{r.text}</p>
                </li>
              ))}
            </ul>

            <label data-testid="proctoring-consent-label" className="mt-8 flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-lg p-4 cursor-pointer">
              <Checkbox data-testid="proctoring-consent-checkbox" checked={consent} onCheckedChange={(v) => setConsent(Boolean(v))} className="mt-0.5" />
              <span className="text-sm text-slate-700 leading-relaxed">
                Dichiaro di aver letto e accettato le condizioni della sessione proctored, inclusa l'attivazione
                obbligatoria della webcam, l'acquisizione di snapshot periodici e l'obbligo di schermo intero con
                annullamento del test in caso di mancato rientro entro 3 secondi.
              </span>
            </label>

            <div className="mt-6 flex justify-between">
              <Button data-testid="consent-back-button" variant="outline" onClick={() => navigate("/dashboard")} disabled={busy}>
                Annulla
              </Button>
              <Button data-testid="proctoring-start-button" className="bg-[#E05A47] hover:bg-[#c94836]" disabled={!consent || busy} onClick={start}>
                {busy ? "Avvio sessione..." : "Accetto e avvio il test"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
